/* Profile rail (left of the canvas) — static customer profile. Compact
 * by default; toggle to Detailed for the full B2B picture. Loads via
 * useCustomerForCall when the page has an active callId; renders an
 * empty/loading/error state otherwise. View choice persists in
 * localStorage so an agent's preference sticks across calls. */

import { useEffect, useState, type ReactNode } from "react";

import { ChevronLeftIcon } from "../components/icons";
import { API_URL, fmtPhone } from "../lib/api";
import {
  useCustomerForCall,
  type AgentDeskCustomer,
  type CustomerState,
} from "./useCustomerForCall";

type View = "compact" | "detailed";
const VIEW_KEY = "agent-desk:profile-view";

function loadView(): View {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    if (v === "compact" || v === "detailed") return v;
  } catch {
    /* fall through */
  }
  return "compact";
}

export function ProfileRail({
  width,
  callId,
  state: stateProp,
  onCollapse,
}: {
  width: number;
  callId: number | undefined;
  /** Optional pre-fetched customer state. AgentDesk hoists the hook so
   *  ProfileRail and AskCoPilot share the lookup. When omitted (e.g. used
   *  outside the agent-desk page) the rail does its own fetch. */
  state?: CustomerState;
  onCollapse: () => void;
}) {
  const internal = useCustomerForCall(stateProp ? undefined : callId);
  const state: CustomerState = stateProp ?? internal;
  const [view, setView] = useState<View>(loadView);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      /* private mode — fine */
    }
  }, [view]);

  return (
    <aside
      id="agent-desk-profile-pane"
      className="agent-desk-pane agent-desk-rail-left"
      style={{ width: `${width}px`, flex: `0 0 ${width}px` }}
    >
      <div className="agent-desk-pane-header">
        <ViewToggle view={view} onChange={setView} />
        <button
          id="agent-desk-profile-collapse-btn"
          type="button"
          className="agent-desk-pane-collapse"
          onClick={onCollapse}
          aria-label="Collapse profile pane"
          title="Collapse profile pane"
        >
          <ChevronLeftIcon size={14} />
        </button>
      </div>

      <div className="agent-desk-pane-body">
        {state.kind === "idle" && <ProfileEmpty />}
        {state.kind === "loading" && <ProfileLoading />}
        {state.kind === "error" && <ProfileError message={state.message} />}
        {state.kind === "no-customer" && (
          <ProfileUnknown fromNumber={state.fromNumber} />
        )}
        {state.kind === "ready" && (
          <ProfileCard
            customer={state.customer}
            verified={state.verified}
            view={view}
          />
        )}
      </div>
    </aside>
  );
}

// ─── View toggle ───────────────────────────────────────────────────────
function ViewToggle({
  view,
  onChange,
}: {
  view: View;
  onChange: (v: View) => void;
}) {
  return (
    <div
      id="agent-desk-profile-view-toggle"
      className="profile-view-toggle"
      role="tablist"
      aria-label="Profile view"
    >
      <button
        id="agent-desk-profile-view-compact"
        type="button"
        role="tab"
        aria-selected={view === "compact"}
        className={`profile-view-toggle-btn ${view === "compact" ? "is-active" : ""}`}
        onClick={() => onChange("compact")}
      >
        Compact
      </button>
      <button
        id="agent-desk-profile-view-detailed"
        type="button"
        role="tab"
        aria-selected={view === "detailed"}
        className={`profile-view-toggle-btn ${view === "detailed" ? "is-active" : ""}`}
        onClick={() => onChange("detailed")}
      >
        Detailed
      </button>
    </div>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────
function ProfileCard({
  customer,
  verified,
  view,
}: {
  customer: AgentDeskCustomer;
  verified: boolean;
  view: View;
}) {
  return (
    <div id="agent-desk-profile-card" className="profile-card">
      <Identity customer={customer} verified={verified} />
      <Plan customer={customer} />
      <Spend customer={customer} />
      <Risk customer={customer} />
      {view === "detailed" && (
        <>
          {customer.business_name && <Business customer={customer} />}
          {customer.billing_email && <Contact customer={customer} />}
          {customer.contract_end_date && <ContractDetail customer={customer} />}
          <RecentCalls customerId={customer.id} />
          {customer.notes && <Notes customer={customer} />}
        </>
      )}
    </div>
  );
}

// ─── Identity block ────────────────────────────────────────────────────
function Identity({
  customer,
  verified,
}: {
  customer: AgentDeskCustomer;
  verified: boolean;
}) {
  const name = displayName(customer);
  const id = customer.account_number ?? `#${customer.id}`;
  return (
    <section className="profile-section profile-identity">
      <div className="profile-name">{name}</div>
      <div className="profile-meta">
        <span className="profile-meta-id">{id}</span>
        <span className="profile-meta-sep">·</span>
        <span>{tenureLabel(customer.tenure_months)}</span>
        {customer.segment && (
          <>
            <span className="profile-meta-sep">·</span>
            <span className="profile-meta-segment">
              {segmentLabel(customer.segment)}
            </span>
          </>
        )}
      </div>
      <div className="profile-meta-row">
        <span className="profile-meta-phone">{fmtPhone(customer.phone)}</span>
        {verified && (
          <span className="profile-verified" title="Caller phone matched the customer record">
            ✓ Verified via call
          </span>
        )}
      </div>
    </section>
  );
}

// ─── Plan ──────────────────────────────────────────────────────────────
function Plan({ customer }: { customer: AgentDeskCustomer }) {
  return (
    <Section label="Plan">
      <KV k="Name" v={customer.plan ?? "—"} />
      {customer.lines !== null && <KV k="Lines" v={String(customer.lines)} />}
      <KV k="Status" v={statusLabel(customer.status)} />
    </Section>
  );
}

// ─── Spend ─────────────────────────────────────────────────────────────
function Spend({ customer }: { customer: AgentDeskCustomer }) {
  const monthly = customer.monthly_charge_cents / 100;
  const annual = monthly * 12;
  const lifetime = monthly * customer.tenure_months;
  return (
    <Section label="Spend">
      <KV k="Monthly" v={fmtMoney(monthly)} />
      <KV k="Annual (est.)" v={fmtMoney(annual)} />
      <KV k="Lifetime" v={fmtMoney(lifetime)} />
      {customer.clv_score !== null && (
        <KV k="CLV score" v={Math.round(customer.clv_score).toLocaleString()} />
      )}
      {customer.prior_save_attempts !== null && (
        <KV
          k="Prior saves"
          v={String(customer.prior_save_attempts)}
        />
      )}
    </Section>
  );
}

// ─── Risk ──────────────────────────────────────────────────────────────
function Risk({ customer }: { customer: AgentDeskCustomer }) {
  const riskPct = Math.round(customer.churn_risk * 100);
  const riskTier = churnTier(customer.churn_risk);
  const saveable = isSaveable(customer);
  return (
    <Section label="Risk">
      <div className="profile-risk-row">
        <span className="profile-risk-label">Churn risk</span>
        <span className={`profile-risk-value tier-${riskTier}`}>
          {riskTier.toUpperCase()} · {riskPct}
        </span>
      </div>
      <div className="profile-risk-row">
        <span className="profile-risk-label">Saveability</span>
        <span
          className={`profile-risk-value tier-${saveable ? "good" : "high"}`}
        >
          {saveable ? "SAVEABLE" : "HIGH FRICTION"}
        </span>
      </div>
      {customer.contract_end_date && (
        <div className="profile-risk-row">
          <span className="profile-risk-label">Contract end</span>
          <span className="profile-risk-value">
            {fmtMonthYear(customer.contract_end_date)}
          </span>
        </div>
      )}
      {customer.cancel_reason && customer.status !== "active" && (
        <div className="profile-risk-row">
          <span className="profile-risk-label">Reason</span>
          <span className="profile-risk-value tier-high">
            {customer.cancel_reason}
          </span>
        </div>
      )}
    </Section>
  );
}

// ─── Detailed-only ─────────────────────────────────────────────────────
function Business({ customer }: { customer: AgentDeskCustomer }) {
  return (
    <Section label="Business">
      <KV k="Name" v={customer.business_name ?? "—"} />
      {customer.segment && <KV k="Segment" v={segmentLabel(customer.segment)} />}
      {customer.account_number && (
        <KV k="Account" v={customer.account_number} />
      )}
    </Section>
  );
}

function Contact({ customer }: { customer: AgentDeskCustomer }) {
  return (
    <Section label="Primary contact">
      {customer.billing_email && <KV k="Email" v={customer.billing_email} />}
      <KV k="Phone" v={fmtPhone(customer.phone)} />
    </Section>
  );
}

function Notes({ customer }: { customer: AgentDeskCustomer }) {
  return (
    <Section label="Notes">
      <p className="profile-notes">{customer.notes}</p>
    </Section>
  );
}

// Contract end + months remaining + remaining-monthly-spend ("at risk if
// they cancel today") so the agent has the ETF framing in one glance.
// Doesn't compute a literal ETF dollar — that's a contract-specific
// calculation we don't have, and inventing it would mislead. The
// "remaining contract value" label is honest about what the number is.
function ContractDetail({ customer }: { customer: AgentDeskCustomer }) {
  const months = monthsUntil(customer.contract_end_date!);
  const remainingValue =
    months !== null && months > 0
      ? (customer.monthly_charge_cents / 100) * months
      : 0;
  return (
    <Section label="Contract">
      <KV k="Ends" v={fmtMonthYear(customer.contract_end_date!)} />
      {months !== null && (
        <KV
          k="Remaining"
          v={
            months <= 0
              ? "expired"
              : `${months} month${months === 1 ? "" : "s"}`
          }
        />
      )}
      {remainingValue > 0 && (
        <KV k="Remaining value" v={fmtMoney(remainingValue)} />
      )}
    </Section>
  );
}

// Recent inbound/outbound for this customer — last 5 calls. Provides the
// "did we save them before? what did we offer last time?" context the
// agent used to ask AskCoPilot for. Click → call detail page.
type RecentCall = {
  id: number;
  direction: "inbound" | "outbound" | string;
  started_at: string;
  ended_at: string | null;
  outcome: string | null;
};

function RecentCalls({ customerId }: { customerId: number }) {
  const [calls, setCalls] = useState<RecentCall[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/customers/${customerId}/calls?limit=5`,
        );
        if (!r.ok) {
          if (!cancelled) setCalls([]);
          return;
        }
        const data = (await r.json()) as { calls: RecentCall[] };
        if (!cancelled) setCalls(data.calls ?? []);
      } catch {
        if (!cancelled) setCalls([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  if (calls === null) return null;
  if (calls.length === 0) {
    return (
      <Section label="Recent calls">
        <div className="profile-recent-empty">No prior calls on file.</div>
      </Section>
    );
  }
  return (
    <Section label="Recent calls">
      <ul className="profile-recent-list">
        {calls.map((c) => (
          <li key={c.id} className="profile-recent-row">
            <span className={`profile-recent-dir is-${c.direction}`}>
              {c.direction === "outbound" ? "↗" : "↙"}
            </span>
            <span className="profile-recent-date">
              {fmtShortDate(c.started_at)}
            </span>
            <span
              className={`profile-recent-outcome tier-${outcomeTier(c.outcome)}`}
            >
              {outcomeLabel(c.outcome, c.ended_at)}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

// ─── Section + KV primitives ───────────────────────────────────────────
function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="profile-section">
      <div className="profile-section-label">{label}</div>
      <div className="profile-section-body">{children}</div>
    </section>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="profile-kv">
      <span className="profile-kv-key">{k}</span>
      <span className="profile-kv-value">{v}</span>
    </div>
  );
}

// ─── Empty / loading / error / unknown ─────────────────────────────────
function ProfileEmpty() {
  return (
    <div className="profile-state profile-state-empty">
      <div className="profile-state-title">No active call</div>
      <div className="profile-state-hint">
        Customer profile will load when a call is connected.
      </div>
    </div>
  );
}

function ProfileLoading() {
  return (
    <div className="profile-state profile-state-loading" role="status">
      <div className="profile-state-title">Loading profile…</div>
    </div>
  );
}

function ProfileError({ message }: { message: string }) {
  return (
    <div className="profile-state profile-state-error" role="alert">
      <div className="profile-state-title">Could not load profile</div>
      <div className="profile-state-hint">{message}</div>
    </div>
  );
}

function ProfileUnknown({ fromNumber }: { fromNumber: string | null }) {
  return (
    <div className="profile-state profile-state-unknown">
      <div className="profile-state-title">Unknown caller</div>
      {fromNumber && (
        <div className="profile-state-phone">
          <span className="profile-state-phone-label">Calling from</span>
          <strong className="profile-state-phone-value">{fmtPhone(fromNumber)}</strong>
        </div>
      )}
      <div className="profile-state-hint">
        This call isn't linked to a subscriber record. Lookup by phone or
        attach manually once the call is in flight.
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────
function displayName(c: AgentDeskCustomer): string {
  const personal = [c.first_name, c.last_name].filter(Boolean).join(" ");
  if (c.business_name) {
    return personal ? `${c.business_name} — ${personal}` : c.business_name;
  }
  return personal || fmtPhone(c.phone);
}

function tenureLabel(months: number): string {
  if (months < 12) return `${months} mo customer`;
  const yrs = Math.floor(months / 12);
  return `${yrs}-yr customer`;
}

function segmentLabel(s: string): string {
  switch (s) {
    case "smb":         return "SMB";
    case "mid_market":  return "Mid-market";
    case "enterprise":  return "Enterprise";
    default:            return s;
  }
}

function statusLabel(s: string): string {
  switch (s) {
    case "active":         return "Active";
    case "pending_cancel": return "Pending cancel";
    case "cancelled":      return "Cancelled";
    default:               return s;
  }
}

function fmtMoney(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function fmtMonthYear(iso: string): string {
  // contract_end_date is stored as YYYY-MM-DD. Render as "MAR 2027".
  const m = iso.match(/^(\d{4})-(\d{2})/);
  if (!m) return iso;
  const monthIdx = Number(m[2]) - 1;
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${months[monthIdx] ?? ""} ${m[1]}`;
}

function churnTier(risk: number): "low" | "mid" | "high" {
  if (risk < 0.4) return "low";
  if (risk < 0.7) return "mid";
  return "high";
}

// Whole months between today and the contract end date. Returns null
// if the input is malformed; negative-or-zero means contract has expired.
function monthsUntil(iso: string): number | null {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const end = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const months =
    (end.getFullYear() - now.getFullYear()) * 12 +
    (end.getMonth() - now.getMonth());
  return months;
}

// "MAR 4" / "MAR 4, 2025" — drop the year if the call is in the current
// calendar year so the row stays compact in the rail.
function fmtShortDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  const yr = Number(m[1]);
  const mo = months[Number(m[2]) - 1];
  const day = Number(m[3]);
  const thisYear = new Date().getFullYear();
  return yr === thisYear ? `${mo} ${day}` : `${mo} ${day} '${String(yr).slice(2)}`;
}

function outcomeLabel(outcome: string | null, endedAt: string | null): string {
  if (!endedAt) return "in progress";
  if (!outcome) return "—";
  return outcome;
}

function outcomeTier(outcome: string | null): "good" | "mid" | "high" | "neutral" {
  if (!outcome) return "neutral";
  const o = outcome.toLowerCase();
  if (o.includes("saved") || o.includes("retained") || o.includes("renewed")) return "good";
  if (o.includes("escalated") || o.includes("deferred")) return "mid";
  if (o.includes("lost") || o.includes("cancel")) return "high";
  return "neutral";
}

function isSaveable(c: AgentDeskCustomer): boolean {
  if (c.status === "cancelled") return false;
  if (c.churn_risk >= 0.9) return false;
  return true;
}
